import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { Box, Heading, Text, Link, LayoutContext, ImageWithPlaceholder, Icon } from '@lucid/components';
import { withImage, useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from 'types/types';

export type PersonColor = Exclude<Lucid.Theme.Color.Keys, 'white' | 'yellow' | 'lime'>;

export interface PersonProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Set to show the company name - otherwise hide it */
  willShowCompany: boolean;
  /** Sets the color scheme for the people backgrounds */
  backgroundColor?: PersonColor;
  /** Changes the text color for a dark section background */
  isDarkBackground?: boolean;
  /** Controls whether the bio should render */
  shouldHideBio?: boolean;
  /** State from parent to know whether this bio is currently displayed */
  expandedIndex?: number;
  /** The position of this person in the parent section */
  index: number;
  /** Function to set the expanded index state in the parent component */
  setExpandedIndex: React.Dispatch<React.SetStateAction<number>>;
  /** The id of the bio element for aria a11y */
  bioElementId?: string;
  /** The normalized person data to be displayed */
  person: PersonGeneric;
}

export type PersonGeneric = Lucid.Directus.Generated.Person_Translations & {
  id?: string;
  name: string;
  image: Lucid.Directus.Generated.Directus_Files;
  company?: string;
  bio?: string;
  title?: string;
  bioDetails?: { i18nKey: string; value: string };
};

const Person: FC<PropsWithChildren<PersonProps>> = ({
  className,
  person,
  isDarkBackground,
  willShowCompany,
  expandedIndex,
  index,
  setExpandedIndex,
  shouldHideBio = false,
  bioElementId,
}) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_homepage_learn_more_about',
      'mktg_show_less',
      'mktg_leadership_show_more',
      'mktg_leadership_show_less',
    ],
    shortCode,
  });
  const { name, image, company, title } = person;

  const handleOnClick = () => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
  };

  return (
    <Box
      css={theme => ({
        margin: '0 auto',
        maxWidth: '277px',
        display: 'flex',
        flexDirection: 'column',
        scrollMarginBlockStart: `${theme.space.s120}px !important`,
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
      id={`bio-${person.id}`}
    >
      <ImageWithPlaceholder
        isBackgroundImage
        alt={`${name} headshot`}
        aspectRatio="1:1"
        css={theme => ({
          marginBottom: theme.space.s24,
          div: {
            backgroundPosition: 'top',
          },
        })}
        src={withImage(image)}
      />
      <Heading
        as="h3"
        css={theme => ({
          marginBottom: theme.space.s8,
          color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
        })}
        variant="headingM"
      >
        {name}
      </Heading>
      {title && (
        <Text
          css={theme => ({
            marginBottom: theme.space.s8,
            color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
          })}
          variant="paragraphS400"
        >
          {title}
        </Text>
      )}
      {willShowCompany && company && (
        <Text
          css={theme => ({
            marginBottom: theme.space.s8,
            color: isDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
          })}
          variant="labelXS400"
        >
          {company}
        </Text>
      )}
      {!shouldHideBio && (person.bio || person.bioDetails) && (
        <Link
          asButton
          aria-controls={bioElementId}
          aria-expanded={expandedIndex === index}
          aria-label={
            expandedIndex === index
              ? withSmartling('mktg_show_less')
              : withSmartling('mktg_homepage_learn_more_about', { title: name })
          }
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            color: theme.colors.blue[70],
            fontWeight: theme.fontWeights.fw600,
            padding: `${theme.space.s8}px 0`,
            '&:focus': {
              outlineOffset: theme.space.s4,
            },
          })}
          iconAnimation="expand"
          id={`bio-tab-${person.id}`}
          onClick={handleOnClick}
          tabIndex={0}
          variant="basic"
        >
          {withSmartling(`mktg_leadership_show_${expandedIndex === index ? 'less' : 'more'}`)}
          <Icon
            color="blue.70"
            css={theme => ({
              transition: 'transform 0.3s',
              transformOrigin: 'center',
              transform: expandedIndex === index ? 'scaleY(-1)' : 'scaleY(1)',
              width: theme.space.s16,
              marginLeft: theme.space.s8,
            })}
            type="CarotDown"
          />
        </Link>
      )}
    </Box>
  );
};

export { Person };
export default Person;
